import {Component, OnInit} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DadConfig, DadProduct, DadProductPrice, Exchange, MdMarket} from '../../models/models';
import {DadConfigService, DadProductService, ExchangeService, MdMarketService} from '../../services/services';
import {ActivatedRoute} from '@angular/router';
import {AuthenticationService} from '../../../authenticationModule/services/authentication.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UtilsService} from '../../../appSharedModule/services/utils.service';
import {DadProductCrudComponent} from '../dad-product-crud/dad-product-crud.component';
import {DadProductPriceCrudComponent} from '../dad-product-price-crud/dad-product-price-crud.component';
import {BaseClientComponent} from '../../../components/base-client.component';

@Component({
  selector: 'app-dad-config-crud',
  templateUrl: './dad-config-crud.component.html',
  styleUrls: ['./dad-config-crud.component.scss'],
  providers: [DialogService],
  host: {class: 'sym-page'}
})
export class DadConfigCrudComponent extends BaseClientComponent implements OnInit {
  listExchanges: Exchange[];
  listExchangesDropDown: SelectItem[] = [];
  selectedExchange: Exchange;
  markets: MdMarket[];
  action: string;
  loading = true;
  loadError = false;
  dadName: string;
  currentDadConfig: DadConfig;
  error = false;
  dadConfigForm: UntypedFormGroup;
  date8: any;
  timezonesList: SelectItem[];
  reportTypeList: SelectItem[];
  successUpdate = false;
  errorUpdate = false;
  successAdd = false;
  success = false;
  loadingMarkets = true;
  deletedDadProductSuccess = false;
  deletedDadProductPriceSuccess = false;
  errorString: any;
  sectionGeneral = true;
  sectionSchedule = true;
  sectionSettings = true;
  sectionProducts = false;

  constructor(
    private exchangeService: ExchangeService,
    private activatedRoute: ActivatedRoute,
    public authenticationService: AuthenticationService,
    private dadConfigService: DadConfigService,
    private formBuilder: UntypedFormBuilder,
    private utilsService: UtilsService,
    private mdMarketService: MdMarketService,
    public dialogService: DialogService,
    private dadProductService: DadProductService,
    private confirmationService: ConfirmationService) {
    super();
  }

  ngOnInit() {
    this.utilsService.lockUi();
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.initData();
    });
    this.subscriptions.push(response$);
  }

  private initData() {
    this.activatedRoute.paramMap.subscribe(params => {
      this.dadName = params.get('dadName');
      this.action = params.get('action');
      if (this.action !== 'show' && !this.authenticationService.userHasRole('ROLE_MCKAY_SUPER')) {
        location.assign('/#/dad-config/show/' + this.dadName);
      }
      this.initSelectLists();
      this.initializeFormGroup();
    });
  }

  private getCurrentDadConfig() {
    this.dadConfigService.getDadConfig(this.dadName).subscribe({
      next: res => {
        this.currentDadConfig = res[0];
        if (!res[0]) {
          this.redirectionToNotFound();
          return;
        }
        this.initializeFormGroupWithDadConfigValues();
        this.loading = false;
        this.utilsService.unlockUi();
        this.getMarkets();
      }, error: () => {
        this.loadError = true;
        this.utilsService.unlockUi();
        this.utilsService.displayMessage('The DAD config could not be loaded', 'Please try again later.', 'error');
      }
    });
  }

  private getMarkets() {
    this.loadingMarkets = true;
    this.mdMarketService.getMarketsForDadConfig(this.currentDadConfig.name).subscribe({
      next: res => {
        this.markets = res;
        this.markets.forEach(exchange => {
          exchange.dadProducts.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
          exchange.dadProducts.forEach(product => {
            product.dadProductPrices.sort(
              (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
          });
        });
        this.loadingMarkets = false;
      }, error: () => {
        this.markets = [];
        this.loadingMarkets = false;
        this.utilsService.displayMessage('The DAD products could not be loaded', 'Please try again later.', 'error');
      }
    });
  }

  redirectionToNotFound() {
    this.utilsService.redirectToNotFound();
  }

  private initializeFormGroupWithDadConfigValues() {
    this.dadConfigForm.patchValue(this.currentDadConfig);
    this.initializeTimeForms();
  }

  private convertDateToTime(dateIn: string) {
    if (!dateIn || dateIn.length === 0) {
      return '';
    }
    return dateIn.substr(11, 5);
  }

  private initializeTimeForms() {
    this.dadConfigForm.get('creationTime').setValue(this.convertDateToTime(this.currentDadConfig.creationTime));
    this.dadConfigForm.get('secondReminderTime').setValue(this.convertDateToTime(this.currentDadConfig.secondReminderTime));
    this.dadConfigForm.get('thirdReminderTime').setValue(this.convertDateToTime(this.currentDadConfig.thirdReminderTime));
    this.dadConfigForm.get('quincyCutoffStartTime').setValue(this.convertDateToTime(this.currentDadConfig.quincyCutoffStartTime));
    this.dadConfigForm.get('autoReportFirstReminderTime').setValue(this.convertDateToTime(this.currentDadConfig.autoReportFirstReminderTime));
  }

  initSelectLists() {
    this.exchangeService.getExchanges().subscribe({
      next: res => {
        this.listExchanges = res;
        for (const exchange of this.listExchanges) {
          this.listExchangesDropDown.push({
            label: exchange.name,
            value: exchange.name
          });
        }
        this.dadConfigForm.get('exchangesNames').setValue([this.dadName]);

        if (this.isEditOrShowAction) {
          this.getCurrentDadConfig();
        } else {
          this.loading = false;
          this.utilsService.unlockUi();
        }
      },
      error: () => {
        this.loadError = true;
        this.utilsService.unlockUi();
        this.utilsService.displayMessage('The exchanges could not be loaded', 'Please try again later.', 'error');
      }
    });
    this.timezonesList = [
      {label: 'UTC', value: 'UTC'},
      {label: 'America/Chicago', value: 'America/Chicago'},
      {label: 'America/New_York', value: 'America/New_York'},
      {label: 'Europe/Berlin', value: 'Europe/Berlin'},
      {label: 'Europe/London', value: 'Europe/London'},
      {label: 'Europe/Paris', value: 'Europe/Paris'},
      {label: 'Asia/Tokyo', value: 'Asia/Tokyo'},
      {label: 'Asia/Hong_Kong', value: 'Asia/Hong_Kong'},
      {label: 'Asia/Singapore', value: 'Asia/Singapore'},
    ];
    this.reportTypeList = [
      {label: 'ice', value: 'ice'},
      {label: 'eurex', value: 'eurex'},
      {label: 'ose', value: 'ose'},
      {label: 'lse', value: 'lse'},
    ];
  }

  initializeFormGroup() {
    this.dadConfigForm = this.formBuilder.group(
      {
        exchangesNames: ['', [Validators.required]],
        exchangeNameInTitle: ['', Validators.required],
        creationDay: [1, [Validators.required, Validators.pattern('^[0-9]*$')]],
        creationTime: ['00:00', Validators.required],
        autoReportFirstReminderDay: [1, [Validators.required, Validators.pattern('^[0-9]*$')]],
        autoReportFirstReminderTime: ['00:00', Validators.required],
        secondReminderDay: [1, [Validators.required, Validators.pattern('^[0-9]*$')]],
        secondReminderTime: ['00:00', Validators.required],
        thirdReminderDay: [1, [Validators.required, Validators.pattern('^[0-9]*$')]],
        thirdReminderTime: ['00:00', Validators.required],
        quincyCutoffStartDay: [1, [Validators.required, Validators.pattern('^[0-9]*$')]],
        quincyCutoffStartTime: ['00:00', Validators.required],
        quincyCutoffPeriod: [4, [Validators.required, Validators.pattern('^[0-9]*$')]],
        timezone: ['', Validators.required],
        reportType: ['', Validators.required],
        jumpCheckThreshold: ['', Validators.required],
        note: [''],
        canAutoReport: [false],
        canSelfReport: [false],
        canExempt: [false],
        canReportAsSubvendor: [false],
        canCarryForward: [false],
        active: [false],
      },
    );
    if (this.action === 'show') {
      this.dadConfigForm.disable();
    }
  }

  onTimePick($event: any, formControlName: string) {
    const date = new Date($event);
    this.dadConfigForm.get(formControlName).setValue(date.getHours() + ':' + (date.getMinutes() < 10 ? '0' : '') + date.getMinutes());
  }

  onSubmit() {
    if (this.dadConfigForm.invalid) {
      this.dadConfigForm.markAllAsTouched();
      return;
    }
    if (this.action === 'edit') {
      return this.updateDadConfig();
    }
    this.createDadConfig();
  }

  private createDadConfig() {
    this.dadConfigService.createDadConfig(this.dadConfigForm.value).subscribe({
        next: () => {
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          this.success = true;
          this.successAdd = true;
          location.assign('/#/exchanges');
        }, error: error => {
          if (error.status === 400) {
            this.errorUpdate = true;
            this.errorString = 'Exchange name in title already exists.';
          } else {
            this.errorUpdate = true;
            this.errorString = error.message + '. Details:  ' + error.error.detail;
          }
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
      }
    );
  }

  private updateDadConfig() {
    if (this.currentDadConfig.reportType !== this.dadConfigForm.get('reportType').value) {
      if (!confirm('Changing the report type can generate unexpected problems in dad declaration. ' +
        'Are you sure you  want to change report type?')) {
        return;
      }
    }

    this.dadConfigService.updateDadConfig(this.currentDadConfig.id, this.dadConfigForm.value).subscribe(
      () => {
        this.dadConfigForm.markAsPristine();
        this.getMarkets();
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        this.success = true;
        this.successUpdate = true;
        this.errorUpdate = false;
      }, error => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        this.errorUpdate = true;
        this.errorString = error.message + '. Details:  ' + error.error.detail;
      }
    );
    return;
  }

  getDadProductPriceAmount(dadProductPrice: DadProductPrice) {
    if (!dadProductPrice) {
      return '';
    }
    if (dadProductPrice.currency === 'USD') {
      return dadProductPrice.amount + ' $';
    }
    if (dadProductPrice.currency === 'EUR') {
      return dadProductPrice.amount + ' €';
    }
    if (dadProductPrice.currency === 'GBP') {
      return dadProductPrice.amount + ' £';
    }
    if (dadProductPrice.currency === 'JPY') {
      return dadProductPrice.amount + ' ¥';
    }
    return dadProductPrice.amount + ' ' + dadProductPrice.currency;

  }

  showProductPricePopUp(selectedMarket: MdMarket, selectedDadProduct?: DadProduct) {
    const ref = this.openProductPricePopUp(selectedMarket, selectedDadProduct);
    this.productPricePopUpOnClose(ref);
  }

  private openProductPricePopUp(selectedMarket: MdMarket, selectedDadProduct?: DadProduct) {
    const header = 'New Dad Product Price';
    return this.dialogService.open(DadProductPriceCrudComponent, {
      width: '30%',
      height: '72%',
      dismissableMask: true,
      styleClass: 'sym-dialog',
      header: header,
      data: {
        markets: this.markets,
        selectedMarket: selectedMarket,
        selectedDadProduct: selectedDadProduct,
      },
    });
  }

  productPricePopUpOnClose(ref: DynamicDialogRef) {
    ref.onClose.subscribe(res => {
      if (!res) {
        return;
      }
      const market = this.markets.find(m => m.name === res.market);
      const dadProduct = market.dadProducts.find(dp => dp.productCode === res.productCode);
      const newDadProductPrice = new DadProductPrice(res.id, res.amount, res.currency, res.startDate);
      dadProduct.dadProductPrices.push(newDadProductPrice);
      dadProduct.dadProductPrices.sort(
        (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      );
    });
  }

  showPopUp(selectedMarket: MdMarket, selectedDadProduct?: DadProduct, isEditDadProduct?: boolean) {
    let ref;
    if (selectedDadProduct) {
      ref = this.openPopUp(selectedMarket, selectedDadProduct, isEditDadProduct);
    } else {
      ref = this.openPopUp(selectedMarket);
    }
    this.popUpOnClose(ref);
  }

  private openPopUp(selectedMarket: MdMarket, selectedDadProduct?: DadProduct, isEditDadProduct?: boolean) {
    const header = isEditDadProduct ? 'Edit Dad Product' : 'New Dad Product';
    return this.dialogService.open(DadProductCrudComponent, {
      width: '30%',
      height: '72%',
      dismissableMask: true,
      styleClass: 'sym-dialog',
      header: header,
      data: {
        markets: this.markets,
        selectedMarket: selectedMarket,
        selectedDadProduct: selectedDadProduct,
        isEditDadProduct: isEditDadProduct
      },
    });
  }

  private popUpOnClose(ref: DynamicDialogRef) {
    ref.onClose.subscribe(res => {
      if (!res) {
        return;
      }
      const market = this.markets.find(m => m.name === res.market);
      const dadProduct = market.dadProducts.find(dp => dp.id === res.id);
      if (dadProduct) {
        dadProduct.productCode = res.productCode;
        dadProduct.productType = res.productType;
        dadProduct.internalProductCode = res.internalProductCode;
        dadProduct.rebillingProductType = res.rebillingProductType;
        dadProduct.rebillingColor = res.rebillingColor;
        dadProduct.startDate = res.startDate;
        dadProduct.id = res.id;
        return;
      }
      const newDadProduct = new DadProduct(res.productCode, res.productType, res.internalProductCode, res.rebillingProductType,
        res.rebillingColor, new Date(res.startDate), res.id, []);
      market.dadProducts.push(newDadProduct);
      this.markets.forEach(exchange => {
        exchange.dadProducts.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      });
    });
  }

  deleteDadProduct(market: MdMarket, dadProduct: DadProduct) {
    this.confirmationService.confirm({
      key: 'deleteDadProduct',
      header: 'Delete Product',
      message: `You are about to delete <strong>${dadProduct.productCode}</strong> (${dadProduct.productType}).<br><br>
                This may cause unexpected errors on existing DAD declarations. This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.dadProductService.deleteDadProduct(dadProduct.id).subscribe(() => {
          this.deletedDadProductSuccess = true;
          this.success = true;
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          market.dadProducts = market.dadProducts.filter(dp => dp !== dadProduct);
        });
      }
    });
  }

  deleteDadProductPrice(dadProductPrice: DadProductPrice, dadProduct: DadProduct) {
    this.confirmationService.confirm({
      key: 'deleteDadProductPrice',
      header: 'Delete Price',
      message: `You are about to delete the <strong>${dadProductPrice.currency} ${dadProductPrice.amount}</strong> price
                valid from <strong>${new Date(dadProductPrice.startDate).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: '2-digit'})}</strong>
                for product <strong>${dadProduct.productCode}</strong>.<br><br>This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.dadProductService.deleteDadProductPrice(dadProductPrice).subscribe(() => {
          this.deletedDadProductPriceSuccess = true;
          this.success = true;
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          dadProduct.dadProductPrices = dadProduct.dadProductPrices.filter(dpp => dpp !== dadProductPrice);
        });
      }
    });
  }

  get isEditOrShowAction() {
    return ['edit', 'show'].includes(this.action);
  }
}
